import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { isEqual } from 'lodash';
import { MouseEvent, useEffect, useState } from 'react';
import { FilterInput } from './FilterInput';
import { FilterListModal } from './FilterListModal';
import { EnumTableFilter } from './types';
import { useFilterRegistration } from '../WbTable';

const useStyles = makeStyles(() => ({
  optionLabel: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));

const EnumFilterValue = <T,>({
  field,
  value,
  renderValue,
}: EnumTableFilter<T>) => {
  const theme = useTheme();

  // Register this filter's active state with the parent toolbar
  useFilterRegistration(value, field);

  return (
    <Typography style={{ display: 'flex', alignItems: 'center' }}>
      {field}
      {value && value.length > 0 && (
        <>
          <Typography component="span">:</Typography>
          <Typography
            style={{ fontWeight: '600', marginLeft: '5px' }}
            component="span"
          >
            {value.length > 1 ? (
              <Box
                component="span"
                style={{ display: 'flex', gap: 5, alignItems: 'center' }}
              >
                {renderValue(value[0])}
                <Typography
                  variant="caption"
                  style={{
                    width: 18,
                    height: 18,
                    background: theme.palette.secondary.main,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '100%',
                    color: 'white',
                    fontSize: 9,
                    fontWeight: 600,
                  }}
                >
                  +{value.length - 1}
                </Typography>
              </Box>
            ) : (
              value.map(v => renderValue(v))
            )}
          </Typography>
        </>
      )}
    </Typography>
  );
};

export const EnumFilter = <T,>(filter: EnumTableFilter<T>) => {
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState(filter.options);
  const [value, setValue] = useState(filter.value || []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onSearch = (search: string) => {
    setSearchValue(search);
    if (filter.onSearch) setOptions(filter.onSearch(search));
  };
  const closeMenu = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setValue(filter.value || []);
      onSearch('');
    }, 500);
  };
  const onInputClear = () => {
    filter.onChange(filter.defaultValue ?? undefined);
    setValue(filter.defaultValue ?? []);
  };
  const onClear = () => {
    setValue(filter.defaultValue ?? []);
  };
  const onSelectAll = () => {
    const enabledOptions = filter.options.filter(
      option => !filter.isOptionDisabled?.(option),
    );
    setValue(enabledOptions);
  };

  const onApply = () => {
    if (value.length === 0) {
      filter.onChange(undefined);
    } else {
      filter.onChange(value);
    }
    setTimeout(() => {
      onSearch('');
    }, 500);
    setAnchorEl(null);
  };

  const onSelect = (checked: boolean, option: T) => {
    setValue(values =>
      checked ? values.concat(option) : values.filter(v => !isEqual(v, option)),
    );
  };

  useEffect(() => {
    setValue(filter.value || []);
  }, [filter.value]);

  useEffect(() => {
    setOptions(filter.options);
  }, [filter.options]);

  return (
    <Box>
      <FilterInput
        onClick={openMenu}
        onClear={onInputClear}
        isOpened={open}
        selected={Boolean(filter.value?.length)}
        hideClear={filter.hideClear}
        field={filter.field}
      >
        <EnumFilterValue {...filter} />
      </FilterInput>

      <FilterListModal
        onSearch={onSearch}
        searchValue={searchValue}
        onClear={onClear}
        onApply={onApply}
        onSelectAll={onSelectAll}
        style={{ maxHeight: '600px' }}
        PopoverProps={{
          open,
          anchorEl,
          onClose: closeMenu,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        }}
      >
        {options.length <= 0 ? (
          <Typography style={{ paddingBottom: '16px' }}>No options</Typography>
        ) : (
          options.map((option, index) => (
            <FormControlLabel
              classes={{
                label: classes.optionLabel,
              }}
              key={index}
              label={filter.renderOption(option)}
              control={
                <Checkbox
                  color="primary"
                  disabled={filter.isOptionDisabled?.(option) ?? false}
                  checked={Boolean(value.find(v => isEqual(v, option)))}
                  onChange={(_, checked) => onSelect(checked, option)}
                />
              }
            />
          ))
        )}
      </FilterListModal>
    </Box>
  );
};
